const STORAGE_KEY = 'looppanel_screener_drafts';
const ACTIVE_DRAFT_KEY = 'looppanel_active_screener_draft_id';

function readAll() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeAll(drafts) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
}

function uid(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// Upload question types are non-editable in the builder (question text only) —
// the actual upload widget only renders for the end-user taking the screener.
export const UPLOAD_QUESTION_TYPES = ['image', 'video', 'file'];
export const NO_OPTIONS_QUESTION_TYPES = ['text', ...UPLOAD_QUESTION_TYPES];

export function createEmptyQuestion(type = 'single_choice') {
  const base = {
    id: uid('q'),
    type,
    prompt: '',
    required: false,
  };

  if (type === 'text') {
    return {
      ...base,
      textBody: '',
    };
  }

  if (UPLOAD_QUESTION_TYPES.includes(type)) {
    return base;
  }

  const optionCount = type === 'multiple_choice' ? 4 : 2;
  const options = Array.from({ length: optionCount }, (_, index) => ({
    id: uid('opt'),
    label: `Option ${index + 1}`,
    qualifies: index === 0,
    isOther: false,
  }));

  return { ...base, options };
}

export function setActiveScreenerDraftId(id) {
  if (!id) {
    sessionStorage.removeItem(ACTIVE_DRAFT_KEY);
    return;
  }
  sessionStorage.setItem(ACTIVE_DRAFT_KEY, id);
}

export function getActiveScreenerDraftId() {
  return sessionStorage.getItem(ACTIVE_DRAFT_KEY) || null;
}

export function createScreenerDraft({
  id,
  name,
  projectId,
  projectName,
  qualifyMethod,
  questions,
} = {}) {
  const draftId = id || uid('scr');
  const now = Date.now();
  const draft = {
    id: draftId,
    name,
    projectId,
    projectName,
    qualifyMethod,
    questions:
      Array.isArray(questions) && questions.length
        ? questions
        : [createEmptyQuestion('single_choice')],
    createdAt: now,
    updatedAt: now,
  };
  const drafts = readAll();
  drafts[draftId] = draft;
  writeAll(drafts);
  setActiveScreenerDraftId(draftId);
  return draft;
}

export function getScreenerDraft(id) {
  if (!id) return null;
  return readAll()[id] || null;
}

export function updateScreenerDraft(id, patch) {
  const drafts = readAll();
  if (!drafts[id]) return null;
  drafts[id] = {
    ...drafts[id],
    ...patch,
    updatedAt: Date.now(),
  };
  writeAll(drafts);
  return drafts[id];
}

export function upsertScreenerDraft(id, data) {
  const drafts = readAll();
  drafts[id] = {
    ...(drafts[id] || {}),
    ...data,
    id,
    updatedAt: Date.now(),
  };
  writeAll(drafts);
  setActiveScreenerDraftId(id);
  return drafts[id];
}

export function cloneBuilderQuestions(questions = []) {
  return JSON.parse(JSON.stringify(questions || []));
}

function normalizeQuestion(question = {}) {
  const type = question.type || 'single_choice';
  const normalized = {
    type,
    prompt: (question.prompt || '').trim(),
    required: Boolean(question.required),
  };

  if (type === 'text') {
    normalized.textBody = (question.textBody || '').trim();
    return normalized;
  }

  if (UPLOAD_QUESTION_TYPES.includes(type)) {
    return normalized;
  }

  normalized.options = (question.options || []).map((option) => ({
    label: (option.label || '').trim(),
    qualifies: Boolean(option.qualifies),
    isOther: Boolean(option.isOther),
  }));

  return normalized;
}

export function areBuilderQuestionsEqual(left = [], right = []) {
  return (
    JSON.stringify((left || []).map(normalizeQuestion)) ===
    JSON.stringify((right || []).map(normalizeQuestion))
  );
}

/**
 * What the builder would show if we ignored sessionStorage.
 */
export function serverQuestionsFallback(apiQuestions = []) {
  if (Array.isArray(apiQuestions) && apiQuestions.length) {
    return cloneBuilderQuestions(apiQuestions);
  }
  return [createEmptyQuestion('single_choice')];
}

/**
 * Prefer the in-tab working copy over a fresh API payload.
 * Preview → Back reloads the builder; without this, empty/stale API
 * questions overwrite unsaved sessionStorage edits.
 */
export function resolveDraftQuestions(localDraft, apiQuestions = []) {
  if (Array.isArray(localDraft?.questions) && localDraft.questions.length) {
    return localDraft.questions;
  }
  return serverQuestionsFallback(apiQuestions);
}
